import type { AdminConfigValueType } from '@carcheck/shared';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import type { InputHTMLAttributes, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Type-specific value editor for an editing config row (AUT-131, architect
 * guardrail #1). The widget is keyed on the wire `type`:
 *
 *   - `number`  → numeric `<TextField type="number">`
 *   - `string`  → text `<TextField>`
 *   - `boolean` → `<Switch>` toggle (true/false label)
 *   - `json`    → monospace multiline `<TextField>` (a `<textarea>`),
 *                 prefilled `JSON.stringify(value, null, 2)`; the parent
 *                 `JSON.parse`s on save and pins a parse error here.
 *
 * Client validation is TRANSPORT-SANITY ONLY (architect guardrail #4): the
 * number field's browser type rejects non-numeric; the json textarea
 * rejects unparseable on save. Backend ranges (e.g. [0,10000]) are NOT
 * replicated — a 400 is mapped onto `error` by the parent.
 *
 * Controlled: the parent owns the draft string (number/string/json) or the
 * draft boolean. `error` renders the inline helper text (400 message or
 * "Невалидный JSON"). Pressing Enter (non-json) invokes `onSubmit`.
 */
export interface ConfigValueEditorProps {
  type: AdminConfigValueType;
  /** Draft text for number/string/json editors. */
  draft: string;
  onDraftChange: (next: string) => void;
  /** Draft boolean for the toggle editor. */
  boolDraft: boolean;
  onBoolChange: (next: boolean) => void;
  /** Inline error (400 message / "Невалидный JSON"); undefined = no error. */
  error?: string | undefined;
  /** Enter-to-save for the single-line editors. */
  onSubmit: () => void;
  disabled?: boolean | undefined;
}

export const ConfigValueEditor = ({
  type,
  draft,
  onDraftChange,
  boolDraft,
  onBoolChange,
  error,
  onSubmit,
  disabled,
}: ConfigValueEditorProps): ReactElement => {
  const { t } = useTranslation();

  if (type === 'boolean') {
    // A typed const, not an inline literal: `InputHTMLAttributes` has no `data-*`.
    const switchInputProps: InputHTMLAttributes<HTMLInputElement> & { 'data-testid': string } = {
      'aria-label': t('config.editor.toggleLabel'),
      'data-testid': 'config-value-switch',
    };
    return (
      <FormControlLabel
        control={
          <Switch
            checked={boolDraft}
            onChange={(e) => onBoolChange(e.target.checked)}
            disabled={disabled}
            inputProps={switchInputProps}
          />
        }
        label={boolDraft ? t('config.value.true') : t('config.value.false')}
      />
    );
  }

  if (type === 'json') {
    return (
      <TextField
        multiline
        minRows={3}
        size="small"
        fullWidth
        value={draft}
        onChange={(e) => onDraftChange(e.target.value)}
        error={Boolean(error)}
        helperText={error}
        disabled={disabled}
        slotProps={{
          htmlInput: {
            'aria-label': t('config.editor.jsonLabel'),
            'data-testid': 'config-value-input',
            style: { fontFamily: 'monospace', fontSize: 13 },
          },
        }}
      />
    );
  }

  return (
    <TextField
      size="small"
      type={type === 'number' ? 'number' : 'text'}
      value={draft}
      onChange={(e) => onDraftChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onSubmit();
        }
      }}
      error={Boolean(error)}
      helperText={error}
      disabled={disabled}
      slotProps={{
        htmlInput: {
          'aria-label':
            type === 'number' ? t('config.editor.numberLabel') : t('config.editor.stringLabel'),
          'data-testid': 'config-value-input',
        },
      }}
      sx={{ minWidth: 160 }}
    />
  );
};
